import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'
import compact from 'lodash/compact'
import AntSelect from 'antd/lib/select'
import 'antd/lib/select/style/index.less'

import SearchBar, { searchProps, SearchContainer } from 'components/SearchBar'
import UniversalLink from 'components/UniversalLink'

import baseTheme from 'theme'
import media from 'media'

import { NAV_BAR_HEIGHT, CLASS_NO_LINK_STYLES, FORMS_SLUG } from 'consts'

import IconHome from '@anvilco-pkg/ui/components/Icon/IconHome'

export const CLASS_NAME = 'form-sidebar'
const SEARCH_HEIGHT = 33

const Container = styled(Box).attrs({
  // width: [1, 1, 0.4],
  className: CLASS_NAME,
})`
  ${media.giant`
    max-width: 250px;
  `}
`

const StyledSearchBar = styled(SearchBar)`
  background: ${baseTheme.colors.blacks[5]};
  margin-left: 12px !important;
  height: ${SEARCH_HEIGHT}px;

  input {
    height: ${SEARCH_HEIGHT}px;
    background: ${baseTheme.colors.blacks[5]};
    color: ${baseTheme.colors.blacks[40]};
  }

  ${SearchContainer} {
    border: none !important;
    padding-left: 10px;

    svg g,
    svg path {
      fill: ${baseTheme.colors.blacks[30]};
    }
  }

  ${media.giant`
    width: 135px !important;
    margin-left: 0 !important;
  `}

  @media (min-width: 1094px) {
    width: 154px !important;
    margin-left: 12px !important;
  }
`

export const Menu = styled(Flex).attrs({
  flexDirection: 'column',
  pr: [0],
})`
  overflow: auto;
  position: sticky;
  top: ${NAV_BAR_HEIGHT + 20}px;
  height: 100%;
  max-height: calc(100vh - ${NAV_BAR_HEIGHT}px);

  ::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

const MenuItem = styled(Flex)
  .attrs({
    width: [1],
    justifyContent: 'space-between',
    my: 5,
    alignItems: 'center',
  })
  .withConfig({
    shouldForwardProp: (prop) => ['isActive'].indexOf(prop) === -1,
  })`
  display: none;

  ${({ isActive }) =>
    isActive &&
    `
    font-weight: ${baseTheme.fontWeights.bold};
    text-decoration: underline !important;
    text-decoration-color: ${baseTheme.colors.green} !important;
    text-decoration-thickness: 3px !important;
    text-underline-offset: 6px !important;
  `}

  ${media.giant`
    display: flex;
  `}

  &:hover {
    cursor: pointer;
    color: ${({ theme }) =>
      theme?.textColors?.linkHover || baseTheme.textColors.linkHover};    
    font-weight: ${baseTheme.fontWeights.bold};

    svg,
    path {
      fill: ${({ theme }) =>
        theme?.textColors?.linkHover || baseTheme.textColors.linkHover};    
      font-weight: ${baseTheme.fontWeights.bold};
    }
  }
`

const SearchAndFilterContainer = styled(Flex)
  .attrs({
    flexDirection: ['row'],
    alignItems: 'center',
    mb: [30, 45, 45, 10],
  })
  .withConfig({
    shouldForwardProp: (prop) => ['numChildren'].indexOf(prop) === -1,
  })`
  height: ${SEARCH_HEIGHT}px;

  & > * {
    width: 100%;

    :first-child {
      margin-right: 12px;
    }
  }
  

  ${media.desktop`
    & > * {
      width: 100%;
    }
  `}
`

const Label = styled.div`
  text-transform: uppercase;
  font-size: 12px;
  font-weight: ${baseTheme.fontWeights.light};
  margin-top: 15px;
  margin-bottom: 10px;
  letter-spacing: 2px;
  display: none;

  ${media.giant`
    display: block;
  `}
`

const IconButton = styled(UniversalLink).attrs({
  className: CLASS_NO_LINK_STYLES,
})`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${baseTheme.colors.blacks[30]};
  width: ${SEARCH_HEIGHT}px;
  min-width: ${SEARCH_HEIGHT}px;
  height: ${SEARCH_HEIGHT}px;

  &&& {
    margin-right: 0;
  }

  g,
  path {
    fill: white;
  }

  :hover {
    background: ${baseTheme.colors.blacks[50]};
  }

  ${media.giant`
      display: none;
  `}

  @media (min-width: 1094px) {
    display: flex;
  }
`

const Dropdown = styled(AntSelect)`
  color: ${baseTheme.colors.blacks[40]} !important;
  height: ${SEARCH_HEIGHT}px;
  border-left: 2px solid ${baseTheme.colors.blacks[30]};

  .ant-select-selector {
    background: ${baseTheme.colors.blacks[5]} !important;
    border: none !important;
    height: ${SEARCH_HEIGHT}px !important;
    box-shadow: none !important;
  }

  .ant-select-selection-item {
    height: ${SEARCH_HEIGHT}px;
    display: flex;
    align-items: center;
  }

  ${media.giant`
    display: none !important;
  `};
`
const { Option } = AntSelect
const DropdownOption = styled(Option)``

const Sidebar = ({
  search,
  menuItems,
  selectedItem,
  isPageTitle,
  filterTitle,
  categoriesLabel,
  hideSearchbar,
  onChange,
  homeSlug = FORMS_SLUG,
  onAllClick,
  ...others
}) => {
  return (
    <Container {...others}>
      <Menu>
        {!hideSearchbar && (
          <SearchAndFilterContainer numChildren={compact([search]).length}>
            <IconButton to={`/${homeSlug}/`} onClick={onAllClick}>
              <IconHome />
            </IconButton>
            {search && (
              <>
                <StyledSearchBar
                  placeholder={search.placeholder}
                  onSearch={search.onSearch}
                  results={search.results}
                  query={search.query}
                  localSearchData={search.localSearchData}
                  searchOptions={{
                    idsOnly: true,
                    idFieldName: 'slug',
                  }}
                />
                <Dropdown
                  defaultValue={selectedItem}
                  selectedValue={selectedItem}
                  onChange={(value) => onChange(value)}
                >
                  {menuItems.map(({ key, value, onClick, as, ...others }) => (
                    <DropdownOption key={key} value={value}>
                      {value}
                    </DropdownOption>
                  ))}
                </Dropdown>
              </>
            )}
          </SearchAndFilterContainer>
        )}
        <Label>{categoriesLabel}</Label>
        {menuItems.map(({ key, value, onClick, as, ...others }) => (
          <MenuItem
            key={key}
            onClick={onClick}
            isActive={selectedItem === value}
            as={as}
            {...others}
            className={CLASS_NO_LINK_STYLES}
            data-cy="form-sidebar-category-item"
          >
            {value}
          </MenuItem>
        ))}
      </Menu>
    </Container>
  )
}

const subMenuItemShape = {
  key: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func,
}

export const menuItemShape = {
  ...subMenuItemShape,
  subMenuItems: PropTypes.arrayOf(PropTypes.shape(subMenuItemShape)),
}

Sidebar.propTypes = {
  isPageTitle: PropTypes.bool,
  ...searchProps,
  menuItems: PropTypes.arrayOf(PropTypes.shape(menuItemShape)).isRequired,
  homeSlug: PropTypes.string,
  onAllClick: PropTypes.func,
}

export default Sidebar
